import * as R from "ramda";
import { Action } from "redux";
import { AppState, DEFAULT_APP_STATE } from "./appState";
import ACTION_TYPE from "./ActionTypes";
import NOTIFICATION_TYPE from "./NotificationType";
import { APP } from "../constants";

export type ResetType =
  | "SOFT_RESET"
  | "HARD_RESET"
  | "TERMINAL_RESET"
  | "OSD_FILTER_RESET"
  | "MY_ASSIGN_FILTER_RESET"
  | "SHORTAGES_FILTER_RESET"
  | "DAMAGES_FILTER_RESET"
  | "DASHBOARD_SEARCH_FILTER_RESET"
  | "ON_HAND_DELIVERY_FILTER_RESET"
  | "MISDELIVERY_FILTER_RESET";

export type ResetStateDispatch = {
  type: ACTION_TYPE.RESET_APP_STATE;
  resetType: ResetType;
};

/**
 * Redux action to reset the app state.
 * @param resetType - Will determine if a full reset of app state is needed or partial if user is to
 * arrive from the ArrivalAPI.
 */
export const resetState = (resetType: ResetType): ResetStateDispatch => ({
  type: ACTION_TYPE.RESET_APP_STATE,
  resetType: resetType,
});

/// declare properties for SOFT reset
type PartialState = {
  activeTerminal?: AppState["activeTerminal"];
  tabPosition?: AppState["tabPosition"];
  message?: AppState["message"];
  notificationType?: AppState["notificationType"];
  notificationTime?: AppState["notificationTime"];
  notificationChange?: AppState["notificationChange"];
  overageListPageObject?: AppState["overageListPageObject"];
  myAssignmentPageObject?: AppState["myAssignmentPageObject"];
  overageSortObjectData?: AppState["overageSortObjectData"];
  myAssignmentSortObjectData?: AppState["myAssignmentSortObjectData"];
  overagesFilterForm?: AppState["overagesFilterForm"];
  myAssignmentsFilterForm?: AppState["myAssignmentsFilterForm"];
  shortagesFilterForm?: AppState["shortagesFilterForm"];
  onHandDeliveryFilterForm?: AppState["onHandDeliveryFilterForm"];
  onHandDeliverySortObject?: AppState["onHandDeliverySortObject"];
  onHandDeliveryPageObject?: AppState["onHandDeliveryPageObject"];
  damagesFilterForm?: AppState["damagesFilterForm"];
  exceptionSearchFilterForm?: AppState["exceptionSearchFilterForm"];
  searchExceptionPageObject?: AppState["searchExceptionPageObject"];
  searchExceptionSortObject?: AppState["searchExceptionSortObject"];
  damagesSortObject?:AppState["damagesSortObject"];
  damagesPageObject?:AppState["damagesPageObject"];
  shortagesSortObject?: AppState["shortagesSortObject"];
  shortagesPageObject?: AppState["shortagesPageObject"];
  terminalMaster?: AppState["terminalMaster"];
  overagesFilterDateData?: AppState["overagesFilterDateData"];
  commodityMasterData?: AppState["commodityMasterData"];
  packageOptionMasterData?: AppState["packageOptionMasterData"];
  exceptionCodeMasterData?: AppState["exceptionCodeMasterData"];
  valueRangeMasterData?: AppState["valueRangeMasterData"];
  investigationStatusMasterData?: AppState["investigationStatusMasterData"];
  shortageStatusMasterData?: AppState["shortageStatusMasterData"];
  myAssignmentStatusMasterData?: AppState["myAssignmentStatusMasterData"];
  misdeliveryFilterForm?: AppState["misdeliveryFilterForm"];
  _persist: AppState["_persist"];
};

/// declare state of the properties which will apply after SOFT reset
export const SOFT_RESET: PartialState = {
  message: "",
  notificationTime: "",
  notificationChange: "",
  activeTerminal: { terminalId: "", region: null },
  notificationType: NOTIFICATION_TYPE.SUCCESS,
  terminalMaster: [],
  overagesFilterDateData: [],
  commodityMasterData: [],
  packageOptionMasterData: [],
  exceptionCodeMasterData: [],
  valueRangeMasterData: [],
  investigationStatusMasterData: [],
  shortageStatusMasterData: [],
  myAssignmentStatusMasterData: [],
  overageListPageObject: DEFAULT_APP_STATE.overageListPageObject,
  myAssignmentPageObject: DEFAULT_APP_STATE.myAssignmentPageObject,
  overageSortObjectData: DEFAULT_APP_STATE.overageSortObjectData,
  myAssignmentSortObjectData: DEFAULT_APP_STATE.myAssignmentSortObjectData,
  overagesFilterForm: DEFAULT_APP_STATE.overagesFilterForm,
  myAssignmentsFilterForm: DEFAULT_APP_STATE.myAssignmentsFilterForm,
  shortagesFilterForm: DEFAULT_APP_STATE.shortagesFilterForm,
  shortagesSortObject: DEFAULT_APP_STATE.shortagesSortObject,
  shortagesPageObject: DEFAULT_APP_STATE.shortagesPageObject,
  damagesFilterForm: DEFAULT_APP_STATE.damagesFilterForm,
  onHandDeliveryFilterForm: DEFAULT_APP_STATE.onHandDeliveryFilterForm,
  onHandDeliverySortObject: DEFAULT_APP_STATE.onHandDeliverySortObject,
  onHandDeliveryPageObject: DEFAULT_APP_STATE.onHandDeliveryPageObject,
  damagesSortObject: DEFAULT_APP_STATE.damagesSortObject,
  damagesPageObject: DEFAULT_APP_STATE.shortagesPageObject,
  exceptionSearchFilterForm: DEFAULT_APP_STATE.exceptionSearchFilterForm,
  searchExceptionPageObject: DEFAULT_APP_STATE.searchExceptionPageObject,
  searchExceptionSortObject: DEFAULT_APP_STATE.searchExceptionSortObject,
  misdeliveryFilterForm: DEFAULT_APP_STATE.misdeliveryFilterForm,
  _persist: undefined,
};

/// declare state of the properties which will apply after SOFT reset
export const TERMINAL_RESET: PartialState = {
  overageSortObjectData: APP.sortModel,
  overageListPageObject: DEFAULT_APP_STATE.overageListPageObject,
  myAssignmentSortObjectData: APP.sortModel,
  myAssignmentPageObject: DEFAULT_APP_STATE.myAssignmentPageObject,
  overagesFilterForm: DEFAULT_APP_STATE.overagesFilterForm,
  myAssignmentsFilterForm: DEFAULT_APP_STATE.myAssignmentsFilterForm,
  shortagesFilterForm: DEFAULT_APP_STATE.shortagesFilterForm,
  shortagesSortObject: DEFAULT_APP_STATE.shortagesSortObject,
  shortagesPageObject: DEFAULT_APP_STATE.shortagesPageObject,
  damagesFilterForm: DEFAULT_APP_STATE.damagesFilterForm,
  damagesSortObject: DEFAULT_APP_STATE.damagesSortObject,
  damagesPageObject: DEFAULT_APP_STATE.damagesPageObject,
  onHandDeliveryFilterForm: DEFAULT_APP_STATE.onHandDeliveryFilterForm,
  onHandDeliverySortObject: DEFAULT_APP_STATE.onHandDeliverySortObject,
  onHandDeliveryPageObject: DEFAULT_APP_STATE.onHandDeliveryPageObject,
  misdeliveryFilterForm: DEFAULT_APP_STATE.misdeliveryFilterForm,
  _persist: undefined,
};

/// declare state of the properties which will apply after SOFT reset
export const OSD_FILTER_RESET: PartialState = {
  overagesFilterForm: DEFAULT_APP_STATE.overagesFilterForm,
  _persist: undefined,
};

/// declare state of the properties which will apply after SOFT reset
export const MY_ASSIGN_FILTER_RESET: PartialState = {
  myAssignmentsFilterForm: DEFAULT_APP_STATE.myAssignmentsFilterForm,
  _persist: undefined,
};

/// declare state of the properties which will apply after SOFT reset
export const SHORTAGES_FILTER_RESET: PartialState = {
  shortagesFilterForm: DEFAULT_APP_STATE.shortagesFilterForm,
  _persist: undefined,
};

/// declare state of the properties which will apply after SOFT reset
  export const DAMAGES_FILTER_RESET: PartialState = {
    damagesFilterForm: DEFAULT_APP_STATE.damagesFilterForm,
    _persist: undefined,
  };

// reset DASHBOARD_SEARCH_FILTER
  export const DASHBOARD_SEARCH_FILTER_RESET: PartialState = {
    exceptionSearchFilterForm: DEFAULT_APP_STATE.exceptionSearchFilterForm,
    searchExceptionPageObject: DEFAULT_APP_STATE.searchExceptionPageObject,
    searchExceptionSortObject: DEFAULT_APP_STATE.searchExceptionSortObject,
    _persist: undefined,
  };

export const ON_HAND_DELIVERY_FILTER_RESET: PartialState = {
  onHandDeliveryFilterForm: DEFAULT_APP_STATE.onHandDeliveryFilterForm,
  _persist: undefined,
};

export const MISDELIVERY_FILTER_RESET: PartialState = {
  misdeliveryFilterForm: DEFAULT_APP_STATE.misdeliveryFilterForm,
  _persist: undefined,
};

const resetTypeMap: Record<string, Partial<AppState>> = {
  SOFT_RESET: SOFT_RESET,
  HARD_RESET: DEFAULT_APP_STATE,
  TERMINAL_RESET: TERMINAL_RESET,
  OSD_FILTER_RESET: OSD_FILTER_RESET,
  MY_ASSIGN_FILTER_RESET: MY_ASSIGN_FILTER_RESET,
  SHORTAGES_FILTER_RESET: SHORTAGES_FILTER_RESET,
  DAMAGES_FILTER_RESET: DAMAGES_FILTER_RESET,
  ON_HAND_DELIVERY_FILTER_RESET: ON_HAND_DELIVERY_FILTER_RESET,
  DASHBOARD_SEARCH_FILTER_RESET: DASHBOARD_SEARCH_FILTER_RESET,
  MISDELIVERY_FILTER_RESET: MISDELIVERY_FILTER_RESET, // Reuse dashboard search filter reset for now
};

export const resetStateReducer = (
  state: AppState,
  action: Action
): AppState => {
  if (action.type !== ACTION_TYPE.RESET_APP_STATE) {
    return state;
  }

  const { resetType } = action as ResetStateDispatch;
  const resetPatch = resetTypeMap[resetType];

  return resetPatch ? R.mergeRight(state, resetPatch) : state;
};